import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Segmented, type SegmentedOption } from "@/shared/ui";

import type { MarketFilterValue } from "../model/storage";

/**
 * Фильтр рынка в каталоге: «Все / Spot / Futures».
 *
 * «Все» — отдельный сегмент, а не отсутствие выбора. Раньше фильтр снимался
 * повторным кликом по активному пункту: выйти из него было можно, но узнать
 * об этом — только случайно, и состояние «фильтра нет» ничем на экране не
 * обозначалось. Три пункта показывают текущий срез сами.
 *
 * `flex-1` на пунктах раздаёт `Segmented`, поэтому три подписи делят строку
 * поровну и на 320 px.
 *
 * `min-h-[42px]` — это требование ТЗ «интерактивная высота не менее 40 px».
 * Своей высоты у пункта 38 px (13/18 плюс `py-2.5`), и до сорока их доводит
 * растяжка: контейнер `items-stretch`, границы съедают два пикселя, значит
 * кнопке достаётся ровно 40. Правка местная, а не в `Segmented`: тот же
 * компонент стоит ещё в пяти местах, где высота подобрана под свой экран.
 */
const OPTIONS: readonly MarketFilterValue[] = ["all", "spot", "futures"];

interface MarketFilterProps {
  value: MarketFilterValue;
  onChange: (value: MarketFilterValue) => void;
}

export function MarketFilter({ value, onChange }: MarketFilterProps) {
  const { t } = useTranslation();

  const options = useMemo<readonly SegmentedOption<MarketFilterValue>[]>(
    () => OPTIONS.map((option) => ({ value: option, label: t(`robots.market.${option}`) })),
    [t],
  );

  return (
    <Segmented
      options={options}
      value={value}
      onChange={onChange}
      ariaLabel={t("robots.filterMarket")}
      className="min-h-[42px] min-w-0 flex-1"
    />
  );
}
